import type { ValidationIssue } from '../types';
import type { Screen } from '../../../shared/stores/useUIStore';
import { resolveValidationDisplayNumber } from '../validationConstants';
import { resolveValidationRepairRoutingPolicy } from './validationRepairRoutingPolicy';

export type GuidedActionKind =
  | 'openMappingConfiguration'
  | 'openQuickFill'
  | 'openMenu'
  | 'openConnectorBinding'
  | 'openProcessProperties'
  | 'openBoltConnection'
  | 'openValidation'
  | 'openModel'
  | 'reconnectPair'
  | 'reconnectAll'
  | 'reviewModelValues'
  | 'none';

export interface GuidedActionConfig {
  kind: GuidedActionKind;
  label: string;
  description: string;
  actionable: boolean;
  elementIds?: string[];
  validationId?: number;
}

export function hasGuidedActionCapability(validationId: number): boolean {
  return resolveValidationDisplayNumber(validationId) != null;
}

function getReconnectGuidedActionForIssue(issue: ValidationIssue): GuidedActionConfig | null {
  const upperMessage = (issue.Message || '').toUpperCase();
  const isReconnectPairIssue =
    issue.ElementIds.length >= 2 &&
    (issue.ValidationId === 1 ||
      (issue.ValidationId === 22 &&
        (upperMessage.includes('SYSTEM BREAK') || upperMessage.includes('DISCONNECTED'))));

  if (isReconnectPairIssue) {
    return {
      kind: 'reconnectPair',
      label: 'Reconnect pair',
      description: 'Attempt to reconnect the detected connector pair directly, then rerun validation.',
      actionable: true,
      elementIds: issue.ElementIds.slice(0, 2),
      validationId: issue.ValidationId,
    };
  }

  return null;
}

export function getGuidedActionForIssue(issue: ValidationIssue): GuidedActionConfig | null {
  const reconnectAction = getReconnectGuidedActionForIssue(issue);
  if (reconnectAction) {
    return reconnectAction;
  }

  const routing = resolveValidationRepairRoutingPolicy(issue);

  switch (routing.reason) {
    case 'mapping-value-coverage':
      return {
        kind: 'openQuickFill',
        label: 'Add model values',
        description: 'Open Quick Fill, populate mapped parameter values on model elements, then rerun validation.',
        actionable: true,
      };
    case 'mapping-missing-model-field':
    case 'mapping-incomplete':
      return {
        kind: 'openMappingConfiguration',
        label: 'Open Mapping',
        description: 'Open Mapping to fix missing or invalid PCF mappings.',
        actionable: true,
      };
    case 'bolt-parameters':
      return {
        kind: 'openBoltConnection',
        label: 'Open bolt connection',
        description: 'Go to Bolt connection to complete bolt-related values.',
        actionable: true,
      };
    case 'pipe-spec-system':
      return {
        kind: 'openProcessProperties',
        label: 'Open process properties',
        description: 'Go to Process properties to align process specification values.',
        actionable: true,
      };
    case 'system-name-collision':
      return {
        kind: 'openValidation',
        label: 'Open Validation',
        description: 'Review the conflicting system names in validation detail and make the export file keys unique.',
        actionable: true,
      };
    case 'required-export-missing':
    case 'format-review':
    case 'parameter-review':
    case 'pipe-spec-connection':
    case 'object-type-category-match':
    case 'component-classification':
      return {
        kind: 'openQuickFill',
        label: 'Open Quick Fill',
        description:
          routing.reason === 'object-type-category-match'
            ? 'Go to Quick Fill to choose OBJECT-TYPE values valid for each affected Revit category, then rerun validation.'
            : 'Go to Quick Fill to review and stage the affected classification or export values.',
        actionable: true,
      };
    case 'end-type-review':
      return {
        kind: 'openValidation',
        label: 'Open Validation',
        description:
          'Review the END-TYPE mismatches case by case in the validation repair workspace before applying the exact fix.',
        actionable: true,
      };
    case 'connector-breaks':
    case 'connector-angle':
    case 'orphaned-elements':
    case 'connector-size':
    case 'system-assignment':
    case 'geometry-overlap':
    case 'system-name':
      return {
        kind: 'openModel',
        label: 'Open in model',
        description: 'Focus the failing model elements and complete the guided repair in-model before revalidation.',
        actionable: true,
      };
    case 'manual-review':
      return null;
    default:
      return {
        kind: 'openValidation',
        label: 'Open Validation',
        description: 'Continue guided review in the validation workspace for this issue.',
        actionable: true,
      };
  }
}

export function resolveGuidedActionScreen(action: GuidedActionConfig): Screen | null {
  switch (action.kind) {
    case 'openMappingConfiguration':
      return 'mappingConfiguration';
    case 'openQuickFill':
      return 'quickFillProperties';
    case 'openMenu':
      return 'menu';
    case 'openConnectorBinding':
      return 'connectorBinding';
    case 'openProcessProperties':
      return 'processProperties';
    case 'openBoltConnection':
      return 'boltConnection';
    case 'openValidation':
      return 'pcfValidation';
    default:
      return null;
  }
}

export function executeGuidedAction(
  action: GuidedActionConfig,
  navigateTo: (screen: Screen) => void
): void {
  if (!action.actionable) {
    return;
  }

  const targetScreen = resolveGuidedActionScreen(action);
  if (targetScreen) {
    navigateTo(targetScreen);
  }
}
